"use client";

import { useRouter } from "next/navigation";
import { useState, useEffect } from "react";
import { Button } from "@/components/themes/ui/button";
import CartItemCard from "@/components/themes/cart-item-card";
import { axiosGetWishlist, AxiosPost } from "@/utilities/axioscall";
import { addtocartclicks } from "@/clicks/productinfoClicks";
import { handleSetCouponInfoStorageItems } from "@/utilities/storageManager";
import { AK, APPENUMS, SK } from "@/constants";
import { ArrowLeft } from "lucide-react";
import { CartInfoDefaults, useUser } from "@/providers/userContext";

interface CartItemsSectionProps {
  isLoading: boolean;
  userData: any;
  cartItems: any[];
  itemCount: number;
}

/* ------------------------------------------------------------------ */
/*  Helper – always returns numbers, never strings                     */
/* ------------------------------------------------------------------ */


export default function CartItemsSection({
  isLoading,
  userData,
  cartItems,
  itemCount,
}: CartItemsSectionProps) {
  const router = useRouter();
  const [cartcouponlist, setCartCouponList] = useState<any[]>([]);
  const [productwishlistData, setProductWishlistData] = useState<any[]>([]);
  const [count, setCount] = useState(0);
  const { cartinfoData, setCartInfoData } = useUser();

  /* --------------------------------------------------------------- */
  /*  1. Quantity change – updates context + localStorage + server   */
  /* --------------------------------------------------------------- */
  /* --------------------------------------------------------------- */
  /*  1. Quantity change – type-safe, numbers only                    */
  /* --------------------------------------------------------------- */
  const handleQuantityChange = async (
    product: any,
    newQuantity: number,
    index: number
  ) => {
    if (newQuantity < 1 || newQuantity > 10) return;

    /* ---- safe fallback when cartinfoData is null ---- */
    const base = cartinfoData ?? {
      products: [],
      cartcount: 0,
      cartquantitycount: 0,
      cartprice: 0,
      subtotal: 0,
      mrptotal: 0,
      shipping: 0,
      ordertotal: 0,
      total: 0,
      shippingrate: "0",
    };

    /* ---- clone & update the product ---- */
    const newProducts = [...base.products];
    const prod = { ...newProducts[index] };
    prod.cartquantity = newQuantity;               // <-- number
    newProducts[index] = prod;

    /* ---- recalc everything (numbers only) ---- */
    const subtotal = newProducts.reduce(
      (s, p) => s + parseFloat(p.sp || "0") * (Number(p.cartquantity) || 1),
      0
    );
    const mrptotal = newProducts.reduce(
      (s, p) => s + parseFloat(p.mrp || "0") * (Number(p.cartquantity) || 1),
      0
    );
    const shipping = newProducts.length === 0 || subtotal >= 299 ? 0 : 45;
    const ordertotal = Math.max(0, subtotal + shipping);
    const total = ordertotal;

    const updatedCart: CartInfoDefaults = {
      products: newProducts,
      cartcount: newProducts.length,
      cartquantitycount: newProducts.reduce(
        (s, p) => s + (Number(p.cartquantity) || 1),
        0
      ),
      cartprice: subtotal,               // <-- required number
      subtotal,
      mrptotal,
      shipping,
      ordertotal,
      total,
      shippingrate: shipping === 0 ? "FREE" : shipping.toFixed(2),
    };

    /* ---- context + localStorage ---- */
    setCartInfoData(updatedCart);
    localStorage.setItem(SK.CARTINFODATA, JSON.stringify(updatedCart));
    localStorage.setItem("cartItems", JSON.stringify(updatedCart.products));

    /* ---- server sync (optional) ---- */
    if (userData?.userid) {
      await AxiosPost(AK.USERCARTINFOFETCHPUSHAPI, {
        userId: userData.userid,
        cartItems: updatedCart.products,
      });
    }
  };

  /* --------------------------------------------------------------- */
  /*  2. Remove item – type-safe, numbers only                      */
  /* --------------------------------------------------------------- */
  const handleRemoveCartChange = async (product: any, index: number) => {
    const base = cartinfoData ?? {
      products: [],
      cartcount: 0,
      cartquantitycount: 0,
      cartprice: 0,
      subtotal: 0,
      mrptotal: 0,
      shipping: 0,
      ordertotal: 0,
      total: 0,
      shippingrate: "0",
    };

    const newProducts = base.products.filter((_: any, i: number) => i !== index);

    const subtotal = newProducts.reduce(
      (s, p) => s + parseFloat(p.sp || "0") * (Number(p.cartquantity) || 1),
      0
    );
    const mrptotal = newProducts.reduce(
      (s, p) => s + parseFloat(p.mrp || "0") * (Number(p.cartquantity) || 1),
      0
    );
    const shipping = newProducts.length === 0 || subtotal >= 299 ? 0 : 45;
    const ordertotal = Math.max(0, subtotal + shipping);
    const total = ordertotal;

    const updatedCart: CartInfoDefaults = {
      products: newProducts,
      cartcount: newProducts.length,
      cartquantitycount: newProducts.reduce(
        (s, p) => s + (Number(p.cartquantity) || 1),
        0
      ),
      cartprice: subtotal,
      subtotal,
      mrptotal,
      shipping,
      ordertotal,
      total,
      shippingrate: shipping === 0 ? "FREE" : shipping.toFixed(2),
    };

    setCartInfoData(updatedCart);
    localStorage.setItem(SK.CARTINFODATA, JSON.stringify(updatedCart));
    localStorage.setItem("cartItems", JSON.stringify(updatedCart.products));

    /* ---- clear coupons ---- */
    handleSetCouponInfoStorageItems([]);
    setCartCouponList([]);
    setCount((c) => c + 1);

    if (userData?.userid) {
      await AxiosPost(AK.USERCARTINFOFETCHCLEARAPI, {
        userId: userData.userid,
        productId: product.id,
      });
    }
  };

  /*  3. Wishlist fetch                                              */
  /* --------------------------------------------------------------- */
  const fetchproductaddon = async () => {
    await axiosGetWishlist(setProductWishlistData);
  };
  useEffect(() => {
    fetchproductaddon();
  }, []);

  /* --------------------------------------------------------------- */
  /*  UI                                                             */
  /* --------------------------------------------------------------- */
  if (isLoading) {
    return (
      <div className="w-full md:w-7/12 lg:!w-7/12 left-side p-4">
        <div className="bg-[#D1F5DB] !rounded-[8px] !py-[10px] !px-[16px] flex items-center !space-x-[10px]">
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <g clipPath="url(#clip0_6_493)">
                <path
                  d="M19.0808 10.0007L19.9582 7.71208C20.0545 7.45959 19.9795 7.17336 19.7682 7.00213L17.8659 5.45973L17.4809 3.03865C17.4384 2.77116 17.2284 2.56243 16.9609 2.51993L14.5398 2.13496L12.9987 0.231338C12.8287 0.0201029 12.5362 -0.0548919 12.29 0.0413515L10.0001 0.920041L7.71155 0.0426014C7.45782 -0.0548919 7.17409 0.0226028 7.00285 0.232588L5.46045 2.13621L3.03937 2.52118C2.77314 2.56368 2.56315 2.77366 2.52065 3.0399L2.13568 5.46098L0.23206 7.00338C0.0220746 7.17336 -0.0541702 7.45959 0.0420732 7.71208L0.919513 10.0007L0.0420732 12.2893C-0.0554201 12.5417 0.0220746 12.828 0.23206 12.998L2.13568 14.5391L2.52065 16.9602C2.56315 17.2277 2.77189 17.4377 3.03937 17.4802L5.46045 17.8651L7.00285 19.7675C7.17409 19.98 7.46032 20.055 7.7128 19.9575L10.0001 19.0813L12.2887 19.9587C12.3612 19.9862 12.4362 20 12.5125 20C12.6962 20 12.8774 19.9187 12.9987 19.7675L14.5398 17.8651L16.9609 17.4802C17.2284 17.4377 17.4384 17.2277 17.4809 16.9602L17.8659 14.5391L19.7682 12.998C19.9795 12.8267 20.0545 12.5417 19.9582 12.2893L19.0808 10.0007Z"
                  fill="#007F22"
                />
                <path
                  d="M8.12512 8.75093C7.09144 8.75093 6.25024 7.90974 6.25024 6.87606C6.25024 5.84238 7.09144 5.00119 8.12512 5.00119C9.1588 5.00119 9.99999 5.84238 9.99999 6.87606C9.99999 7.90974 9.1588 8.75093 8.12512 8.75093ZM8.12512 6.2511C7.78014 6.2511 7.50016 6.53109 7.50016 6.87606C7.50016 7.22104 7.78014 7.50102 8.12512 7.50102C8.47009 7.50102 8.75007 7.22104 8.75007 6.87606C8.75007 6.53109 8.47009 6.2511 8.12512 6.2511Z"
                  fill="#FAFAFA"
                />
                <path
                  d="M11.8747 15.0004C10.8411 15.0004 9.99988 14.1592 9.99988 13.1255C9.99988 12.0918 10.8411 11.2506 11.8747 11.2506C12.9084 11.2506 13.7496 12.0918 13.7496 13.1255C13.7496 14.1592 12.9084 15.0004 11.8747 15.0004ZM11.8747 12.5005C11.531 12.5005 11.2498 12.7818 11.2498 13.1255C11.2498 13.4692 11.531 13.7504 11.8747 13.7504C12.2185 13.7504 12.4997 13.4692 12.4997 13.1255C12.4997 12.7818 12.2185 12.5005 11.8747 12.5005Z"
                  fill="#FAFAFA"
                />
                <path
                  d="M6.87513 15.0004C6.74889 15.0004 6.62265 14.9629 6.51266 14.8841C6.23143 14.6829 6.16643 14.2929 6.36767 14.0117L12.6172 5.26229C12.8185 4.98106 13.2084 4.91606 13.4897 5.1173C13.7709 5.31729 13.8347 5.70851 13.6347 5.98849L7.3851 14.7379C7.26136 14.9091 7.07012 15.0004 6.87513 15.0004Z"
                  fill="#FAFAFA"
                />
              </g>
              <defs>
                <clipPath id="clip0_6_493">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
          <p className="!text-[#007F22] font-semibold !text-[12px]">
            Loading cart items...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full md:w-7/12 lg:!w-7/12 left-side p-4">
      {/* banner */}
      <div className="!bg-[#D1F5DB] !rounded-[8px] !py-[10px] !px-[16px] flex items-center !space-x-[10px]">
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
          >
            <g clipPath="url(#clip0_6_493)">
              <path
                d="M19.0808 10.0007L19.9582 7.71208C20.0545 7.45959 19.9795 7.17336 19.7682 7.00213L17.8659 5.45973L17.4809 3.03865C17.4384 2.77116 17.2284 2.56243 16.9609 2.51993L14.5398 2.13496L12.9987 0.231338C12.8287 0.0201029 12.5362 -0.0548919 12.29 0.0413515L10.0001 0.920041L7.71155 0.0426014C7.45782 -0.0548919 7.17409 0.0226028 7.00285 0.232588L5.46045 2.13621L3.03937 2.52118C2.77314 2.56368 2.56315 2.77366 2.52065 3.0399L2.13568 5.46098L0.23206 7.00338C0.0220746 7.17336 -0.0541702 7.45959 0.0420732 7.71208L0.919513 10.0007L0.0420732 12.2893C-0.0554201 12.5417 0.0220746 12.828 0.23206 12.998L2.13568 14.5391L2.52065 16.9602C2.56315 17.2277 2.77189 17.4377 3.03937 17.4802L5.46045 17.8651L7.00285 19.7675C7.17409 19.98 7.46032 20.055 7.7128 19.9575L10.0001 19.0813L12.2887 19.9587C12.3612 19.9862 12.4362 20 12.5125 20C12.6962 20 12.8774 19.9187 12.9987 19.7675L14.5398 17.8651L16.9609 17.4802C17.2284 17.4377 17.4384 17.2277 17.4809 16.9602L17.8659 14.5391L19.7682 12.998C19.9795 12.8267 20.0545 12.5417 19.9582 12.2893L19.0808 10.0007Z"
                fill="#007F22"
              />
              <path
                d="M8.12512 8.75093C7.09144 8.75093 6.25024 7.90974 6.25024 6.87606C6.25024 5.84238 7.09144 5.00119 8.12512 5.00119C9.1588 5.00119 9.99999 5.84238 9.99999 6.87606C9.99999 7.90974 9.1588 8.75093 8.12512 8.75093ZM8.12512 6.2511C7.78014 6.2511 7.50016 6.53109 7.50016 6.87606C7.50016 7.22104 7.78014 7.50102 8.12512 7.50102C8.47009 7.50102 8.75007 7.22104 8.75007 6.87606C8.75007 6.53109 8.47009 6.2511 8.12512 6.2511Z"
                fill="#FAFAFA"
              />
              <path
                d="M11.8747 15.0004C10.8411 15.0004 9.99988 14.1592 9.99988 13.1255C9.99988 12.0918 10.8411 11.2506 11.8747 11.2506C12.9084 11.2506 13.7496 12.0918 13.7496 13.1255C13.7496 14.1592 12.9084 15.0004 11.8747 15.0004ZM11.8747 12.5005C11.531 12.5005 11.2498 12.7818 11.2498 13.1255C11.2498 13.4692 11.531 13.7504 11.8747 13.7504C12.2185 13.7504 12.4997 13.4692 12.4997 13.1255C12.4997 12.7818 12.2185 12.5005 11.8747 12.5005Z"
                fill="#FAFAFA"
              />
              <path
                d="M6.87513 15.0004C6.74889 15.0004 6.62265 14.9629 6.51266 14.8841C6.23143 14.6829 6.16643 14.2929 6.36767 14.0117L12.6172 5.26229C12.8185 4.98106 13.2084 4.91606 13.4897 5.1173C13.7709 5.31729 13.8347 5.70851 13.6347 5.98849L7.3851 14.7379C7.26136 14.9091 7.07012 15.0004 6.87513 15.0004Z"
                fill="#FAFAFA"
              />
            </g>
            <defs>
              <clipPath id="clip0_6_493">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>
        <p className="!text-[#007F22] font-semibold !text-[12px]">
          " Your Favorites, Delivered Swiftly to Your Door ! "
        </p>
      </div>

      {itemCount === 0 ? (
        /* empty cart UI – unchanged */
        <div className="border-solid border-2 border-[#EBEBEB] rounded-[8px] p-[10px] mt-6">
          <div className="flex flex-col items-center justify-center h-full py-8 ">
            <img src="ecomsaas-assets/loader/empty-cart.webp" alt="" />
            <h3>
              <b>Cart is empty.</b>
            </h3>
            <Button size="lg" className="w-full md:w-auto bg-primary border border-primary hover:bg-primary_hover text-white rounded-[8px] mt-4">
              <a href="/products">Shop Now</a>
            </Button>
          </div>
        </div>
      ) : (
        <div className="Cart_leftSection !my-[20px] !rounded-[8px]">
          {cartItems.map((product: any, productindex: number) => (
            <CartItemCard
              key={`${product.id}-${product.selectedSize?.code || ""}-${productindex}`}
              item={product}
              itemidex={productindex}
              productwishlistData={productwishlistData}
              setProductWishlistData={setProductWishlistData}
              handleQuantityChange={handleQuantityChange}
              handleRemoveCartChange={handleRemoveCartChange}
              count={count}
              setCount={setCount}
              navigate={router.push}
            />
          ))}
        </div>
      )}
    </div>
  );
}